import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { DataTable } from '@/components/ui/data-table';
import { useToast } from '@/hooks/use-toast';
import { supabase, SUPABASE_URL, SUPABASE_PUBLISHABLE_KEY } from '@/integrations/supabase/client';
import { usePlatformAdmin } from '@/hooks/use-platform-admin';
import { useSupportTickets } from '@/hooks/use-support-tickets';
import type { SupportTicketStatus } from '@/lib/support-service';
import { 
  Shield, 
  Building2, 
  Users, 
  MapPin, 
  Calendar,
  AlertTriangle,
  Settings,
  Key,
  Search,
  RefreshCw,
  Trash2,
  Edit,
  Plus,
  CheckCircle,
  XCircle,
  Globe
} from 'lucide-react';
import { Layout } from '@/components/layout/Layout';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

interface Company {
  id: string;
  name: string;
  has_access: boolean;
  created_at: string;
  userCount?: number;
  locationCount?: number;
}

interface UserProfile {
  id: string;
  email: string;
  full_name: string | null;
  role: string;
  company_id: string | null;
  created_at: string;
  company?: { name: string };
}

interface Location {
  id: string;
  company_id: string;
  name: string;
  address: string | null;
  phone: string | null;
  email: string | null;
  is_active: boolean;
  is_default: boolean;
  created_at: string;
  company?: { name: string };
}

const Admin = () => {
  const { isPlatformAdmin, loading: adminLoading } = usePlatformAdmin();
  const { toast } = useToast();
  const navigate = useNavigate();
  
  const [companies, setCompanies] = useState<Company[]>([]);
  const [users, setUsers] = useState<UserProfile[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const {
    tickets: supportTickets,
    isLoading: supportLoading,
    updateTicketStatus,
    isUpdatingTicketStatus,
  } = useSupportTickets();

  // Modal states
  const [createCompanyOpen, setCreateCompanyOpen] = useState(false);
  const [createUserOpen, setCreateUserOpen] = useState(false);
  const [createLocationOpen, setCreateLocationOpen] = useState(false);

  // Form states
  const [newCompany, setNewCompany] = useState({ name: '', has_access: true });
  const [newUser, setNewUser] = useState({ 
    email: '', 
    full_name: '', 
    role: 'employee',
    company_id: '',
    password: '123456'
  });
  const [newLocation, setNewLocation] = useState({
    name: '',
    company_id: '',
    address: '',
    phone: '',
    email: '',
    is_active: true,
    is_default: false
  });

  useEffect(() => {
    if (!adminLoading && !isPlatformAdmin) {
      toast({
        title: "Erişim Reddedildi",
        description: "Bu sayfaya erişim yetkiniz bulunmamaktadır.",
        variant: "destructive"
      });
      navigate('/');
      return;
    }

    if (isPlatformAdmin) {
      fetchAllData();
    }
  }, [isPlatformAdmin, adminLoading, navigate, toast]);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        fetchCompanies(),
        fetchUsers(),
        fetchLocations()
      ]);
    } finally {
      setLoading(false);
    }
  };

  const fetchCompanies = async () => {
    const { data, error } = await supabase
      .from('companies')
      .select(`
        *,
        user_profiles(count),
        locations(count)
      `)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching companies:', error);
      toast({
        title: "Hata",
        description: "Şirketler yüklenemedi.",
        variant: "destructive"
      });
    } else {
      setCompanies(data.map(company => ({
        ...company,
        userCount: company.user_profiles?.length || 0,
        locationCount: company.locations?.length || 0
      })));
    }
  };

  const fetchUsers = async () => {
    const { data, error } = await supabase
      .from('user_profiles')
      .select(`
        *,
        companies(name)
      `)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching users:', error);
      toast({
        title: "Hata",
        description: "Kullanıcılar yüklenemedi.",
        variant: "destructive"
      });
    } else {
      setUsers(data.map(user => ({
        ...user,
        company: user.companies ? { name: user.companies.name } : undefined
      })));
    }
  };

  const fetchLocations = async () => {
    const { data, error } = await supabase
      .from('locations')
      .select(`
        *,
        companies(name)
      `)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching locations:', error);
      toast({
        title: "Hata",
        description: "Lokasyonlar yüklenemedi.",
        variant: "destructive"
      });
    } else {
      setLocations(data.map(location => ({
        ...location,
        company: location.companies ? { name: location.companies.name } : undefined
      })));
    }
  };

  const toggleCompanyAccess = async (companyId: string, currentAccess: boolean) => {
    const { error } = await supabase
      .from('companies')
      .update({ has_access: !currentAccess })
      .eq('id', companyId);

    if (error) {
      toast({
        title: "Hata",
        description: "Şirket erişimi güncellenemedi.",
        variant: "destructive"
      });
    } else {
      toast({
        title: "Başarılı",
        description: `Şirket erişimi ${!currentAccess ? 'açıldı' : 'kapatıldı'}.`,
      });
      fetchCompanies();
    }
  };

  const resetUserPassword = async (userId: string, userEmail: string) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('No active session');
      }

      const response = await fetch(`${SUPABASE_URL}/functions/v1/admin-users?action=reset-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
          'apikey': SUPABASE_PUBLISHABLE_KEY,
        },
        body: JSON.stringify({
          action: 'reset-password',
          userId: userId,
          password: '123456'
        }),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || result.details || 'Failed to reset password');
      }

      toast({
        title: "Başarılı",
        description: `${userEmail} kullanıcısının şifresi 123456 olarak sıfırlandı.`,
      });
    } catch (error: any) {
      console.error('Error resetting password:', error);
      toast({
        title: "Hata",
        description: error.message || "Şifre sıfırlanamadı.",
        variant: "destructive"
      });
    }
  };

  const deleteUser = async (userId: string, userEmail: string) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('No active session');
      }

      const response = await fetch(`${SUPABASE_URL}/functions/v1/admin-users?action=delete`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
          'apikey': SUPABASE_PUBLISHABLE_KEY,
        },
        body: JSON.stringify({
          action: 'delete',
          userId: userId
        }),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || result.details || 'Failed to delete user');
      }

      toast({
        title: "Başarılı",
        description: `${userEmail} kullanıcısı silindi.`,
      });
      
      fetchUsers();
    } catch (error: any) {
      console.error('Error deleting user:', error);
      toast({
        title: "Hata",
        description: error.message || "Kullanıcı silinemedi.",
        variant: "destructive"
      });
    }
  };

  const createCompany = async () => {
    const { error } = await supabase
      .from('companies')
      .insert([newCompany]);

    if (error) {
      toast({
        title: "Hata",
        description: "Şirket oluşturulamadı.",
        variant: "destructive"
      });
    } else {
      toast({
        title: "Başarılı",
        description: "Şirket oluşturuldu.",
      });
      setCreateCompanyOpen(false);
      setNewCompany({ name: '', has_access: true });
      fetchCompanies();
    }
  };

  const createUser = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('No active session');
      }

      const response = await fetch(`${SUPABASE_URL}/functions/v1/admin-users?action=create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
          'apikey': SUPABASE_PUBLISHABLE_KEY,
        },
        body: JSON.stringify({
          action: 'create',
          email: newUser.email,
          password: newUser.password,
          full_name: newUser.full_name || null,
          role: newUser.role,
          company_id: (newUser.company_id && newUser.company_id !== 'none' && newUser.company_id !== '') ? newUser.company_id : null
        }),
      });

      const result = await response.json();
      
      console.log('Edge function response:', { status: response.status, result });

      if (!response.ok) {
        const errorMessage = result.details 
          ? `${result.error}: ${result.details}` 
          : result.error || 'Failed to create user';
        console.error('Error details:', result);
        throw new Error(errorMessage);
      }

      toast({
        title: "Başarılı",
        description: "Kullanıcı oluşturuldu.",
      });
      
      setCreateUserOpen(false);
      setNewUser({ 
        email: '', 
        full_name: '', 
        role: 'employee',
        company_id: '',
        password: '123456'
      });
      fetchUsers();
    } catch (error: any) {
      console.error('Error creating user:', error);
      toast({
        title: "Hata",
        description: error.message || "Kullanıcı oluşturulamadı.",
        variant: "destructive"
      });
    }
  };

  const createLocation = async () => {
    const { error } = await supabase
      .from('locations')
      .insert([newLocation]);

    if (error) {
      toast({
        title: "Hata",
        description: "Lokasyon oluşturulamadı.",
        variant: "destructive"
      });
    } else {
      toast({
        title: "Başarılı",
        description: "Lokasyon oluşturuldu.",
      });
      setCreateLocationOpen(false);
      setNewLocation({
        name: '',
        company_id: '',
        address: '',
        phone: '',
        email: '',
        is_active: true,
        is_default: false
      });
      fetchLocations();
    }
  };

  if (adminLoading || (!isPlatformAdmin && !adminLoading)) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-full">
          <div className="text-center">
            {adminLoading ? (
              <>
                <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-4" />
                <p>Yetki kontrol ediliyor...</p>
              </>
            ) : (
              <>
                <AlertTriangle className="w-8 h-8 text-destructive mx-auto mb-4" />
                <p>Bu sayfaya erişim yetkiniz bulunmamaktadır.</p>
              </>
            )}
          </div>
        </div>
      </Layout>
    );
  }

  const filteredCompanies = companies.filter(company =>
    company.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredUsers = users.filter(user =>
    user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.company?.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredLocations = locations.filter(location =>
    location.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    location.company?.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredSupportTickets = supportTickets.filter((ticket) => {
    const term = searchTerm.toLowerCase();
    return (
      ticket.title.toLowerCase().includes(term) ||
      ticket.description.toLowerCase().includes(term) ||
      ticket.creator?.full_name?.toLowerCase().includes(term) ||
      ticket.creator?.email?.toLowerCase().includes(term)
    );
  });

  const getSupportStatusVariant = (status: SupportTicketStatus) => {
    if (status === 'open') return 'secondary';
    if (status === 'working') return 'outline';
    return 'default';
  };

  const getSupportStatusLabel = (status: SupportTicketStatus) => {
    if (status === 'open') return 'Açık';
    if (status === 'working') return 'Çalışılıyor';
    return 'Tamamlandı';
  };

  const handleSupportStatusUpdate = async (ticketId: string, status: SupportTicketStatus) => {
    try {
      await updateTicketStatus({ ticketId, status });
      toast({
        title: 'Başarılı',
        description: 'Destek talebi durumu güncellendi.',
      });
    } catch (error: any) {
      console.error('Error updating support ticket status:', error);
      toast({
        title: 'Hata',
        description: error?.message || 'Destek talebi güncellenemedi.',
        variant: 'destructive',
      });
    }
  };

  return (
    <Layout>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-2">
              <Shield className="w-8 h-8 text-yellow-500" />
              Platform Yönetimi
            </h1>
            <p className="text-muted-foreground">
              Tüm platform verilerini yönetin
            </p>
          </div>
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Ara..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 w-64"
              />
            </div>
            <Button 
              onClick={() => navigate('/admin/mars-scraper')} 
              variant="outline"
            >
              <Globe className="w-4 h-4 mr-2" />
              Mars Fiyat Kazıyıcı
            </Button>
            <Button 
              onClick={() => navigate('/admin/arcelik-scraper')} 
              variant="outline"
            >
              <Globe className="w-4 h-4 mr-2" />
              Arçelik Katalog Kazıyıcı
            </Button>
            <Button onClick={fetchAllData} variant="outline" size="icon">
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Toplam Şirket</CardTitle>
              <Building2 className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{companies.length}</div>
              <p className="text-xs text-muted-foreground">
                {companies.filter(c => c.has_access).length} aktif
              </p>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Toplam Kullanıcı</CardTitle>
              <Users className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{users.length}</div>
              <p className="text-xs text-muted-foreground">
                Tüm şirketlerde
              </p>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Toplam Lokasyon</CardTitle>
              <MapPin className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{locations.length}</div>
              <p className="text-xs text-muted-foreground">
                {locations.filter(l => l.is_active).length} aktif
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Main Content */}
        <Tabs defaultValue="companies" className="space-y-4">
          <TabsList>
            <TabsTrigger value="companies">Şirketler</TabsTrigger>
            <TabsTrigger value="users">Kullanıcılar</TabsTrigger>
            <TabsTrigger value="locations">Lokasyonlar</TabsTrigger>
            <TabsTrigger value="support">Destek</TabsTrigger>
          </TabsList>

          {/* Companies Tab */}
          <TabsContent value="companies" className="space-y-4">
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-semibold">Şirket Yönetimi</h2>
              <Dialog open={createCompanyOpen} onOpenChange={setCreateCompanyOpen}>
                <DialogTrigger asChild>
                  <Button>
                    <Plus className="w-4 h-4 mr-2" />
                    Şirket Ekle
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Yeni Şirket Ekle</DialogTitle>
                    <DialogDescription>
                      Platforma yeni bir şirket ekleyin.
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="company-name">Şirket Adı</Label>
                      <Input
                        id="company-name"
                        value={newCompany.name}
                        onChange={(e) => setNewCompany({ ...newCompany, name: e.target.value })}
                        placeholder="Şirket adını girin"
                      />
                    </div>
                    <div className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        id="has-access"
                        checked={newCompany.has_access}
                        onChange={(e) => setNewCompany({ ...newCompany, has_access: e.target.checked })}
                      />
                      <Label htmlFor="has-access">Erişim Yetkisi</Label>
                    </div>
                    <Button onClick={createCompany} className="w-full">
                      Şirket Oluştur
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>

            <Card>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="border-b">
                      <tr>
                        <th className="text-left p-4">Şirket</th>
                        <th className="text-left p-4">Oluşturulma</th>
                        <th className="text-left p-4">Kullanıcı</th>
                        <th className="text-left p-4">Lokasyon</th>
                        <th className="text-left p-4">Erişim</th>
                        <th className="text-left p-4">İşlemler</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredCompanies.map((company) => (
                        <tr key={company.id} className="border-b hover:bg-muted/50">
                          <td className="p-4 font-medium">{company.name}</td>
                          <td className="p-4 text-sm text-muted-foreground">
                            {new Date(company.created_at).toLocaleDateString('tr-TR')}
                          </td>
                          <td className="p-4">
                            <Badge variant="secondary">{company.userCount || 0}</Badge>
                          </td>
                          <td className="p-4">
                            <Badge variant="secondary">{company.locationCount || 0}</Badge>
                          </td>
                          <td className="p-4">
                            <Badge variant={company.has_access ? "default" : "destructive"}>
                              {company.has_access ? "Aktif" : "Kapalı"}
                            </Badge>
                          </td>
                          <td className="p-4">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => toggleCompanyAccess(company.id, company.has_access)}
                            >
                              {company.has_access ? "Kapat" : "Aç"}
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Users Tab */}
          <TabsContent value="users" className="space-y-4">
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-semibold">Kullanıcı Yönetimi</h2>
              <Dialog open={createUserOpen} onOpenChange={setCreateUserOpen}>
                <DialogTrigger asChild>
                  <Button>
                    <Plus className="w-4 h-4 mr-2" />
                    Kullanıcı Ekle
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Yeni Kullanıcı Ekle</DialogTitle>
                    <DialogDescription>
                      Platforma yeni bir kullanıcı ekleyin.
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="user-email">Email</Label>
                      <Input
                        id="user-email"
                        type="email"
                        value={newUser.email}
                        onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                        placeholder="kullanici@email.com"
                      />
                    </div>
                    <div>
                      <Label htmlFor="user-name">Ad Soyad</Label>
                      <Input
                        id="user-name"
                        value={newUser.full_name}
                        onChange={(e) => setNewUser({ ...newUser, full_name: e.target.value })}
                        placeholder="Ad Soyad"
                      />
                    </div>
                    <div>
                      <Label htmlFor="user-role">Rol</Label>
                      <Select
                        value={newUser.role}
                        onValueChange={(value) => setNewUser({ ...newUser, role: value })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="admin">Yönetici</SelectItem>
                          <SelectItem value="employee">Çalışan</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="user-company">Şirket</Label>
                      <Select
                        value={newUser.company_id || "none"}
                        onValueChange={(value) => setNewUser({ ...newUser, company_id: value === "none" ? "" : value })}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Şirket seçin" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Şirket Yok</SelectItem>
                          {companies.map((company) => (
                            <SelectItem key={company.id} value={company.id}>
                              {company.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="user-password">Şifre</Label>
                      <Input
                        id="user-password"
                        type="password"
                        value={newUser.password}
                        onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                        placeholder="Şifre"
                      />
                    </div>
                    <Button onClick={createUser} className="w-full">
                      Kullanıcı Oluştur
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>

            <Card>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="border-b">
                      <tr>
                        <th className="text-left p-4">Email</th>
                        <th className="text-left p-4">Ad Soyad</th>
                        <th className="text-left p-4">Rol</th>
                        <th className="text-left p-4">Şirket</th>
                        <th className="text-left p-4">Oluşturulma</th>
                        <th className="text-left p-4">İşlemler</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredUsers.map((user) => (
                        <tr key={user.id} className="border-b hover:bg-muted/50">
                          <td className="p-4 font-medium">{user.email}</td>
                          <td className="p-4">{user.full_name || '-'}</td>
                          <td className="p-4">
                            <Badge variant="secondary">{user.role}</Badge>
                          </td>
                          <td className="p-4">{user.company?.name || '-'}</td>
                          <td className="p-4 text-sm text-muted-foreground">
                            {new Date(user.created_at).toLocaleDateString('tr-TR')}
                          </td>
                          <td className="p-4">
                            <div className="flex gap-2">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => resetUserPassword(user.id, user.email || '')}
                              >
                                <Key className="w-4 h-4 mr-1" />
                                Şifre Sıfırla
                              </Button>
                              <AlertDialog>
                                <AlertDialogTrigger asChild>
                                  <Button variant="destructive" size="sm">
                                    <Trash2 className="w-4 h-4 mr-1" />
                                    Sil
                                  </Button>
                                </AlertDialogTrigger>
                                <AlertDialogContent>
                                  <AlertDialogHeader>
                                    <AlertDialogTitle>Kullanıcıyı Sil</AlertDialogTitle>
                                    <AlertDialogDescription>
                                      {user.email} kullanıcısını silmek istediğinizden emin misiniz? 
                                      Bu işlem geri alınamaz.
                                    </AlertDialogDescription>
                                  </AlertDialogHeader>
                                  <AlertDialogFooter>
                                    <AlertDialogCancel>İptal</AlertDialogCancel>
                                    <AlertDialogAction
                                      onClick={() => deleteUser(user.id, user.email || '')}
                                      className="bg-destructive hover:bg-destructive/90"
                                    >
                                      Sil
                                    </AlertDialogAction>
                                  </AlertDialogFooter>
                                </AlertDialogContent>
                              </AlertDialog>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Locations Tab */}
          <TabsContent value="locations" className="space-y-4">
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-semibold">Lokasyon Yönetimi</h2>
              <Dialog open={createLocationOpen} onOpenChange={setCreateLocationOpen}>
                <DialogTrigger asChild>
                  <Button>
                    <Plus className="w-4 h-4 mr-2" />
                    Lokasyon Ekle
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Yeni Lokasyon Ekle</DialogTitle>
                    <DialogDescription>
                      Bir şirkete yeni lokasyon ekleyin.
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="location-company">Şirket</Label>
                      <Select
                        value={newLocation.company_id}
                        onValueChange={(value) => setNewLocation({ ...newLocation, company_id: value })}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Şirket seçin" />
                        </SelectTrigger>
                        <SelectContent>
                          {companies.map((company) => (
                            <SelectItem key={company.id} value={company.id}>
                              {company.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="location-name">Lokasyon Adı</Label>
                      <Input
                        id="location-name"
                        value={newLocation.name}
                        onChange={(e) => setNewLocation({ ...newLocation, name: e.target.value })}
                        placeholder="Lokasyon adı"
                      />
                    </div>
                    <div>
                      <Label htmlFor="location-address">Adres</Label>
                      <Textarea
                        id="location-address"
                        value={newLocation.address}
                        onChange={(e) => setNewLocation({ ...newLocation, address: e.target.value })}
                        placeholder="Adres"
                      />
                    </div>
                    <div>
                      <Label htmlFor="location-phone">Telefon</Label>
                      <Input
                        id="location-phone"
                        value={newLocation.phone}
                        onChange={(e) => setNewLocation({ ...newLocation, phone: e.target.value })}
                        placeholder="Telefon"
                      />
                    </div>
                    <div>
                      <Label htmlFor="location-email">Email</Label>
                      <Input
                        id="location-email"
                        type="email"
                        value={newLocation.email}
                        onChange={(e) => setNewLocation({ ...newLocation, email: e.target.value })}
                        placeholder="Email"
                      />
                    </div>
                    <div className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        id="location-active"
                        checked={newLocation.is_active}
                        onChange={(e) => setNewLocation({ ...newLocation, is_active: e.target.checked })}
                      />
                      <Label htmlFor="location-active">Aktif</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        id="location-default"
                        checked={newLocation.is_default}
                        onChange={(e) => setNewLocation({ ...newLocation, is_default: e.target.checked })}
                      />
                      <Label htmlFor="location-default">Varsayılan</Label>
                    </div>
                    <Button onClick={createLocation} className="w-full">
                      Lokasyon Oluştur
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>

            <Card>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="border-b">
                      <tr>
                        <th className="text-left p-4">Lokasyon</th>
                        <th className="text-left p-4">Şirket</th>
                        <th className="text-left p-4">Adres</th>
                        <th className="text-left p-4">Telefon</th>
                        <th className="text-left p-4">Durum</th>
                        <th className="text-left p-4">Oluşturulma</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredLocations.map((location) => (
                        <tr key={location.id} className="border-b hover:bg-muted/50">
                          <td className="p-4 font-medium">
                            {location.name}
                            {location.is_default && (
                              <Badge variant="secondary" className="ml-2">Varsayılan</Badge>
                            )}
                          </td>
                          <td className="p-4">{location.company?.name || '-'}</td>
                          <td className="p-4 text-sm">{location.address || '-'}</td>
                          <td className="p-4 text-sm">{location.phone || '-'}</td>
                          <td className="p-4">
                            <Badge variant={location.is_active ? "default" : "destructive"}>
                              {location.is_active ? "Aktif" : "Pasif"}
                            </Badge>
                          </td>
                          <td className="p-4 text-sm text-muted-foreground">
                            {new Date(location.created_at).toLocaleDateString('tr-TR')}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="support" className="space-y-4">
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-semibold">Destek Talepleri</h2>
              <Badge variant="secondary">{filteredSupportTickets.length}</Badge>
            </div>

            <Card>
              <CardContent className="p-0">
                {supportLoading ? (
                  <div className="p-6 text-center text-muted-foreground">Destek talepleri yükleniyor...</div>
                ) : filteredSupportTickets.length === 0 ? (
                  <div className="p-6 text-center text-muted-foreground">Gösterilecek destek talebi yok.</div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="border-b">
                        <tr>
                          <th className="text-left p-4">Başlık</th>
                          <th className="text-left p-4">Kullanıcı</th>
                          <th className="text-left p-4">Oluşturulma</th>
                          <th className="text-left p-4">Durum</th>
                          <th className="text-left p-4">Ekler</th>
                          <th className="text-left p-4">İşlemler</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredSupportTickets.map((ticket) => (
                          <tr key={ticket.id} className="border-b hover:bg-muted/50 align-top">
                            <td className="p-4">
                              <div className="font-medium">{ticket.title}</div>
                              <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{ticket.description}</p>
                            </td>
                            <td className="p-4 text-sm">
                              <div>{ticket.creator?.full_name || '-'}</div>
                              <div className="text-muted-foreground">{ticket.creator?.email || '-'}</div>
                            </td>
                            <td className="p-4 text-sm text-muted-foreground">
                              {new Date(ticket.created_at).toLocaleString('tr-TR')}
                            </td>
                            <td className="p-4">
                              <Badge variant={getSupportStatusVariant(ticket.status)}>
                                {getSupportStatusLabel(ticket.status)}
                              </Badge>
                            </td>
                            <td className="p-4">
                              <Badge variant="outline">{ticket.attachments?.length || 0}</Badge>
                            </td>
                            <td className="p-4">
                              <div className="flex gap-2">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  disabled={isUpdatingTicketStatus || ticket.status === 'working' || ticket.status === 'completed'}
                                  onClick={() => handleSupportStatusUpdate(ticket.id, 'working')}
                                >
                                  Çalışılıyor
                                </Button>
                                <Button
                                  size="sm"
                                  disabled={isUpdatingTicketStatus || ticket.status === 'completed'}
                                  onClick={() => handleSupportStatusUpdate(ticket.id, 'completed')}
                                >
                                  Tamamla
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
};

export default Admin;
